import { useState } from "react";
import {
  useDiseases,
  useCreateFamilyDiseaseHistory,
  useDeleteFamilyDiseaseHistory,
} from "@/hooks/useConsultation";
import { useToast } from "@/hooks/use-toast";
import { SearchableChipDialog } from "@/components/consultation/shared/SearchableChipDialog";
import type {
  FamilyDiseaseHistory,
  CreateFamilyDiseaseHistoryRequest,
} from "@/types/appointments";

type RelationType = "FATHER" | "MOTHER" | "SIBLING";

interface FamilyDiseaseHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  patientFamilyHistory?: FamilyDiseaseHistory[];
  relationType: RelationType;
}

const relationConfig: Record<RelationType, { title: string }> = {
  FATHER: { title: "Add Father's Medical History" },
  MOTHER: { title: "Add Mother's Medical History" },
  SIBLING: { title: "Add Sibling's Medical History" },
};

export const FamilyDiseaseHistoryDialog = ({
  open,
  onOpenChange,
  patientId,
  patientFamilyHistory = [],
  relationType,
}: FamilyDiseaseHistoryDialogProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { toast } = useToast();

  const { data: diseases = [] } = useDiseases();
  const createMutation = useCreateFamilyDiseaseHistory();
  const deleteMutation = useDeleteFamilyDiseaseHistory();

  const config = relationConfig[relationType];

  const relationHistory = patientFamilyHistory.filter(
    (h) => h.relations === relationType
  );

  const addedDiseaseIds = new Set(relationHistory.map((h) => h.disease.id));

  const availableDiseases =
    searchTerm.length >= 2
      ? diseases.filter((disease) => {
          const matchesSearch =
            disease.display_name
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            disease.icd10_code
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase());
          const notAdded = !addedDiseaseIds.has(disease.id);
          return matchesSearch && notAdded;
        })
      : [];

  const handleAddDisease = async (diseaseId: string) => {
    try {
      const requestData: CreateFamilyDiseaseHistoryRequest = {
        disease: diseaseId,
        relations: relationType,
        patient: patientId,
      };
      await createMutation.mutateAsync(requestData);
      setSearchTerm("");
      toast({
        title: "Success",
        description: "Family disease history added successfully",
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to add family disease history",
        variant: "destructive",
      });
    }
  };

  const handleRemoveDisease = async (id: string) => {
    const entry = relationHistory.find((h) => h.id === id);
    if (!entry) return;
    try {
      await deleteMutation.mutateAsync({ historyId: entry.id, patientId });
      toast({ title: "Success", description: "Disease removed successfully" });
    } catch {
      toast({
        title: "Error",
        description: "Failed to remove disease",
        variant: "destructive",
      });
    }
  };

  return (
    <SearchableChipDialog
      open={open}
      onOpenChange={onOpenChange}
      title={config.title}
      addedItems={relationHistory.map((h) => ({
        id: h.id,
        label: h.disease.display_name,
      }))}
      onRemoveItem={handleRemoveDisease}
      searchLabel="Add disease here"
      searchPlaceholder="Search Here"
      searchResults={availableDiseases.map((d) => ({
        id: d.id,
        label: d.display_name,
      }))}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      minSearchChars={2}
      emptySearchMessage="No matching diseases found"
      onAddItem={handleAddDisease}
    />
  );
};

export const FatherDiseaseHistoryDialog = (
  props: Omit<FamilyDiseaseHistoryDialogProps, "relationType">
) => <FamilyDiseaseHistoryDialog {...props} relationType="FATHER" />;

export const MotherDiseaseHistoryDialog = (
  props: Omit<FamilyDiseaseHistoryDialogProps, "relationType">
) => <FamilyDiseaseHistoryDialog {...props} relationType="MOTHER" />;

export const SiblingDiseaseHistoryDialog = (
  props: Omit<FamilyDiseaseHistoryDialogProps, "relationType">
) => <FamilyDiseaseHistoryDialog {...props} relationType="SIBLING" />;
